import { PopupCustom } from "@components/generals/popup";
import { Spiner } from "@components/loaders/spiner";
import {
  GroupedData,
  GroupedResult,
  HistoryBalance,
  PocketHistory,
  Transaction,
} from "@services/dtos/finance.dto";
import { FinanceService } from "@services/finance.service";
import { SchoolService } from "@services/school.service";
import { useThemeStore } from "@stores/theme.store";
import { useQuery } from "@tanstack/react-query";
import { toRp } from "@utils/helper";
import { linkPage } from "@utils/router";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { BiFilter } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { BsArrowDownLeft, BsArrowUpRight } from "react-icons/bs";
import { MdArrowForwardIos } from "react-icons/md";

export const CheckingAccountPage = () => {
  const navigate = useNavigate();
  const { setFooter, setHeader, setAppBar, setBottomNavigation } =
    useThemeStore((state) => state);
  const [range, setRange] = useState<"WEEK" | "MONTH">("WEEK");
  const [type, setType] = useState<"ALL" | "IN" | "OUT">("ALL");
  const [limit, setLimit] = useState<"10" | "20" | "50">("10");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const { data: dataSiswa } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    SchoolService.studentBio
  );
  const nis = dataSiswa?.data?.nis ?? "";

  const { data, isLoading } = useQuery(
    [FinanceService.queries.GET_FINANCE_CHECKING_ACCOUNT_OLD, nis, limit, type],
    () => FinanceService.checkingAccountOld({ nis, limit, type })
  );

  const { groupedData, dataWithMetadata } = useMemo<GroupedResult>(() => {
    // Make sure data exists
    if (!data?.data) return { groupedData: {}, dataWithMetadata: [] };
    const rawData = data?.data;

    // Parse nominal to number AND add month/day properties
    const parsedData: Transaction[] = rawData.map((item: HistoryBalance) => ({
      ...item,
      nominal:
        typeof item.nominal === "string"
          ? parseInt(item.nominal)
          : item.nominal,
      month: item.tanggal.substring(0, 7), // Extract YYYY-MM
      day: item.tanggal,
    }));

    // Group by month
    const byMonth = _.groupBy(parsedData, "month");
    const result: GroupedData = {};

    Object.keys(byMonth).forEach((month) => {
      result[month] = {};

      // Group by day within each month
      const byDay = _.groupBy(byMonth[month], "day");

      Object.keys(byDay).forEach((day) => {
        result[month][day] = {};

        // Group by filter within each day
        const byFilter = _.groupBy(byDay[day], "filter");

        Object.keys(byFilter).forEach((filter) => {
          // Sum nominal for each filter group
          result[month][day][filter] = _.sumBy(byFilter[filter], "nominal");
        });
      });
    });

    // Return BOTH groupedData and parsedData
    return { groupedData: result, dataWithMetadata: parsedData };
  }, [data]);

  useEffect(() => {
    setBottomNavigation("home");
    setFooter(false);
    setHeader({
      type: "small",
      title: "Rekening Koran",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ leading: "back", title: "Rekening Koran" });
  }, [setFooter, setHeader, setAppBar, setBottomNavigation]);

  const onOpenDetail = (month: string, day: string, filter: string) => {
    navigate(linkPage.FINANCE_CHECKING_ACCOUNT_DETAIL, {
      state: {
        month,
        day,
        filter,
        data: groupedData[month]?.[day]?.[filter] || 0,
        // Use dataWithMetadata instead of data?.data
        transactions: dataWithMetadata.filter(
          (item: Transaction) =>
            item.month === month && item.day === day && item.filter === filter
        ),
      },
    });
  };

  return (
    <>
      <div className="card card-style">
        <div className="content mb-0 mt-3">
          <div className="d-flex justify-content-between align-items-center my-2">
            <p className="mb-0 tw-w-2/3 tw-text-black dark:tw-text-gray-300">
              {/* Data difilter dari{" "}
              {moment()
                .subtract(
                  1,
                  (range.toLowerCase() +
                    "s") as moment.unitOfTime.DurationConstructor
                )
                .format("DD MMMM YYYY")}{" "}
              - {moment().format("DD MMMM YYYY")} */}
              Jumlah data yang ditampilkan: {limit} data terakhir
            </p>
            <button
              className="tw-text-white tw-bg-gray-700 hover:tw-bg-gray-600 tw-rounded-lg tw-p-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold"
              onClick={() => setPopupFilter(true)}
            >
              <BiFilter size={22} />
            </button>
          </div>

          {isLoading ? (
            <div className="d-flex p-3">
              <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
            </div>
          ) : data?.data != null && data.data.length > 0 ? (
            <div className="list-group list-group-flush">
              {Object.keys(groupedData)
                .sort()
                .reverse()
                .map((month) => (
                  <div key={month} className="mb-4">
                    <div className="tw-text-black dark:tw-text-white tw-text-lg tw-font-semibold">
                      {new Date(month + "-01").toLocaleDateString("id-ID", {
                        year: "numeric",
                        month: "long",
                      })}
                    </div>

                    {Object.keys(groupedData[month])
                      .sort()
                      .reverse()
                      .map((day) => (
                        <div key={day} className="mb-2">
                          <div className="tw-border-[0px] tw-border-b tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-border-solid">
                            {Object.keys(groupedData[month][day]).map(
                              (filter) => (
                                <div
                                  key={filter}
                                  className="tw-flex tw-items-center tw-gap-4 tw-justify-between tw-py-3"
                                  onClick={() =>
                                    onOpenDetail(month, day, filter)
                                  }
                                >
                                  <div className="tw-flex tw-items-center tw-justify-center">
                                    <div
                                      className={`tw-p-3 tw-rounded-full ${
                                        filter === "IN"
                                          ? "tw-bg-green-900/70 tw-text-green-400"
                                          : "tw-bg-red-900/70 tw-text-red-400"
                                      }`}
                                    >
                                      {filter === "IN" ? (
                                        <BsArrowDownLeft size={20} />
                                      ) : (
                                        <BsArrowUpRight size={20} />
                                      )}
                                    </div>
                                    <div className="tw-ms-4">
                                      <div
                                        className={`font-semibold ${
                                          filter === "IN"
                                            ? "tw-text-green-600"
                                            : "tw-text-red-600"
                                        }`}
                                      >
                                        {filter === "IN"
                                          ? "Uang Masuk"
                                          : "Uang Keluar"}
                                      </div>
                                      <div className="tw-text-xs tw-text-black dark:tw-text-gray-300">
                                        {new Date(day).toLocaleDateString(
                                          "id-ID",
                                          {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          }
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="tw-flex tw-items-center tw-justify-center">
                                    <div
                                      className={`tw-font-bold tw-text-sm ${
                                        filter === "IN"
                                          ? "tw-text-green-600"
                                          : "tw-text-red-600"
                                      }`}
                                    >
                                      {filter === "IN" ? "+" : "-"}
                                      {filter === "IN"
                                        ? toRp(
                                            groupedData[month][day][filter] +
                                              3000
                                          )
                                        : toRp(groupedData[month][day][filter])}
                                    </div>

                                    <MdArrowForwardIos
                                      size={20}
                                      className="my-auto ms-2"
                                    />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          ) : (
            <div className="tw-p-8 text-center ">
              <FcFinePrint size={50} />
              <div>Data tidak ditemukan</div>
            </div>
          )}
        </div>
      </div>

      <PopupCustom
        show={popupFilter}
        position="bottom"
        height={320}
        onCancel={() => setPopupFilter(false)}
      >
        <div className="pb-2 px-3">
          <h4 className="font-700 mt-3">Filter Riwayat</h4>
          <div className="divider mt-3 mb-3"></div>

          <div className="input-style has-borders input-style-always-active no-icon mb-4">
            <label htmlFor="form5" className="color-highlight font-500">
              Jumlah ditampilkan
            </label>
            <select
              defaultValue="10"
              onChange={(e) => setLimit(e.target.value as "10" | "20" | "50")}
              className="bg-theme"
            >
              <option value="default" disabled>
                Jumlah ditampilkan
              </option>
              <option value="10">10 Data Terakhir</option>
              <option value="20">20 Data Terakhir</option>
              <option value="50">50 Data Terakhir</option>
            </select>
            <span>
              <i>
                <FaChevronDown />
              </i>
            </span>
          </div>
          <div className="input-style has-borders input-style-always-active no-icon mb-4">
            <label htmlFor="form5" className="color-highlight font-500">
              Tipe Transaksi
            </label>
            <select
              onChange={(e) => setType(e.target.value as "ALL" | "IN" | "OUT")}
              defaultValue="ALL"
              className="bg-theme"
            >
              <option value="default" disabled>
                Tipe Transaksi
              </option>
              <option value="ALL">Semua Transaksi</option>
              <option value="IN">Uang Masuk</option>
              <option value="OUT">Uang Keluar</option>
            </select>
            <span>
              <i>
                <FaChevronDown />
              </i>
            </span>
          </div>
          <div className="divider !tw-my-2" />
          <button
            className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-red-dark tw-grow my-3 tw-w-full tw-h-11"
            onClick={() => setPopupFilter(false)}
          >
            Tutup
          </button>
        </div>
      </PopupCustom>
    </>
  );
};
